import React, { useState } from 'react';
import { Button, Form, Typography, Modal, Grid, Spin } from 'antd';
import { PhoneOutlined, PhoneFilled } from '@ant-design/icons';
import PhoneInput from 'react-phone-number-input';
import metadata from 'libphonenumber-js/metadata.full.json';
import 'react-phone-number-input/style.css';
import { useTranslation } from 'react-i18next';
import { getCurrentUser } from '../../utils/userStorage';
import useCallDialer from '../../hooks/useCallDialer';

const { Paragraph, Title } = Typography;
const { useBreakpoint } = Grid;

export const PLACEHOLDERS = {
  AR: '11 2345 6789',
  CO: '312 3456789',
  CL: '9 8765 4321',
  MX: '55 1234 5678',
};

export const getPhonePlaceholder = (country = null) => {
  const user = getCurrentUser();
  const currentCountry = country || user?.calls_country || user?.survey_country;
  if (!currentCountry) return null;
  return PLACEHOLDERS[currentCountry] || null;
};

export default function CallDialer({ onCallEnded, patient = null }) {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const patientName = patient?.getFullName();
  const {
    form,
    ui: {
      rawNumber,
      phoneError,
      callStatus,
      isCallModalVisible,
      timer,
      activeCallTarget,
      defaultCountry,
    },
    handlers: {
      handlePhoneChange,
      handleSubmit,
      endCall,
    },
  } = useCallDialer({ patient, onCallEnded });

  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);

  const handleCountryChange = (newCountry) => {
    setSelectedCountry(newCountry);
    handlePhoneChange('');
  };

  return (
    <>
      {!patient && (
        <div style={{
          maxWidth: '500px', margin: '20px auto', background: '#80808020',
          padding: '20px', borderRadius: '16px',
        }}>
          <Form
            form={form}
            onFinish={handleSubmit}
            layout="vertical"
            style={{ maxWidth: '400px', margin: '0 auto' }}
          >
            <Form.Item
              label={t('calls.dialer_title')}
              name="to_number"
              validateStatus={phoneError ? 'error' : ''}
              help={phoneError}
              style={{ marginBottom: 0 }}
              labelCol={{ style: { paddingBottom: 5 } }}
            >
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <PhoneInput
                    countries={['AR', 'CO', 'CL', 'MX']}
                    defaultCountry={selectedCountry}
                    metadata={metadata}
                    smartCaret
                    value={rawNumber}
                    onChange={handlePhoneChange}
                    onCountryChange={setSelectedCountry}
                    placeholder={getPhonePlaceholder(selectedCountry)}
                    className="custom-phone-input"
                  />
                </div>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={callStatus === 'connecting'}
                  icon={<PhoneOutlined style={{ 
                    transform: 'rotate(90deg)',
                    fontSize: screens.sm ? 16 : 20
                  }} />}
                  style={{
                    borderRadius: '50%', padding: 0, background: 'var(--main-color)',
                    border: 'none', boxShadow: '0 4px 12px rgba(76,175,80,0.3)',
                    width: 56, height: 56, fontSize: 20,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}
                />
              </div>
            </Form.Item>
          </Form>
        </div>
      )}

      <Modal
        open={isCallModalVisible}
        footer={null}
        closable={false}
        centered
        maskClosable={false}
        width={360}
      >
        <div style={{
          textAlign: 'center', display: 'flex', flexDirection: 'column',
          alignItems: 'center', gap: 24, background: '#80808012',
          padding: 20, borderRadius: 7,
        }}>
          <Title level={5} style={{ margin: 0 }}>
            {callStatus === 'connecting'
              ? t('calls.connecting')
              : `${t('calls.in_progress')} ${activeCallTarget}`
            }
          </Title>

          {callStatus === 'connecting'
            ? <Spin size="large" />
            : <div style={{ fontSize: 24, fontWeight: 100, marginBottom: 16 }}>
              {timer}
            </div>
          }

          {callStatus === 'connected' && (
            <>
              <Button
                type="primary" danger
                icon={<PhoneFilled style={{ transform: 'rotate(225deg)' }} />}
                onClick={endCall}
                size="large" shape="circle"
                style={{
                  marginTop: 20, width: 64, height: 64,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 24, background: '#ff4d4f', border: 'none',
                  boxShadow: '0 4px 12px rgba(255,77,79,0.3)',
                }}
              />
              <Paragraph type="secondary" style={{ margin: 0 }}>
                {t('calls.transcription_note')}
              </Paragraph>
            </>
          )}
        </div>
      </Modal>
    </>
  );
}